/**
 * Scroll locking utility for the document body.
 *
 * Responsibilities:
 * - Lock the page scroll by fixing the body and compensating the scrollbar width
 * - Restore previous body styles and scroll position on unlock
 * - Optionally toggle the historic global flag `window.checkForScroll`
 *
 * Usage:
 * import scrollLock, { ScrollLock } from './scroll-lock';
 * scrollLock.lock();
 * // ... later
 * scrollLock.unlock();
 */
class ScrollLock {
    /**
     * @constructor
     */
    constructor () {
        /** @type {number} */
        this._scrollY = 0;
        /** @type {boolean} */
        this._isLocked = false;
        /** @type {{position:string, top:string, paddingRight:string}} */
        this._prevStyles = { position: '', top: '', paddingRight: '' };
        /** @type {number|null} */
        this._cachedScrollbarWidth = null;
        /** @type {number} */
        this._globalFlagDelay = 500; // ms
    }

    /**
     * Whether the body is currently scroll-locked.
     * @returns {boolean}
     */
    get isLocked () { return this._isLocked; }

    /**
     * Lock page scroll. Adds padding to compensate the missing scrollbar to prevent layout shift.
     * Also sets `window.checkForScroll = false` (historic behavior) unless disabled via option.
     *
     * @param {Object} [options]
     * @param {boolean} [options.setGlobalFlag=true] Whether to set `window.checkForScroll = false` on lock.
     * @returns {void}
     */
    lock (options = {}) {
        if (this._isLocked) return;
        const { setGlobalFlag = true } = options;
        const body = document?.body;
        if (!body) return;

        // Save previous inline styles to restore later
        this._prevStyles = {
            position: body.style.position,
            top: body.style.top,
            paddingRight: body.style.paddingRight
        };

        const scrollbarWidth = this.getScrollbarWidth();
        // Additive padding-right compensation to avoid layout shift
        if (scrollbarWidth > 0) {
            const currentPR = parseFloat(getComputedStyle(body).paddingRight || '0') || 0;
            body.style.paddingRight = `${currentPR + scrollbarWidth}px`;
        }

        // Remember scroll position and lock body
        this._scrollY = window.scrollY ?? document.documentElement.scrollTop ?? 0;
        body.classList.add('scroll-lock-active');
        body.style.position = 'fixed';
        body.style.top = `-${this._scrollY}px`;

        if (setGlobalFlag) {
            // historic behavior used in other modules
            window.checkForScroll = false;
        }

        this._isLocked = true;
    }

    /**
     * Unlock page scroll. Restores previous body styles and scroll position.
     * Also resets `window.checkForScroll = true` after a small delay (configurable).
     *
     * @param {Object} [options]
     * @param {boolean} [options.restoreGlobalFlag=true] Whether to restore `window.checkForScroll` to true.
     * @returns {void}
     */
    unlock (options = {}) {
        if (!this._isLocked) return;
        const { restoreGlobalFlag = true } = options;
        const body = document?.body;
        if (!body) return;

        // Restore previous inline styles
        body.style.position = this._prevStyles.position || '';
        body.style.top = this._prevStyles.top || '';
        body.style.paddingRight = this._prevStyles.paddingRight || '';

        // Restore scroll position
        window.scrollTo(0, this._scrollY);

        if (restoreGlobalFlag) {
            setTimeout(() => { window.checkForScroll = true; }, this._globalFlagDelay);
        }

        body.classList.remove('scroll-lock-active');
        this._isLocked = false;
    }

    /**
     * Toggle lock state.
     * @param {boolean} [force] If provided, forces the state: true → lock, false → unlock.
     * @returns {void}
     */
    toggle (force) {
        if (typeof force === 'boolean') {
            force ? this.lock() : this.unlock();
            return;
        }
        this._isLocked ? this.unlock() : this.lock();
    }

    /**
     * Configure the delay for restoring the historic global flag (`window.checkForScroll`).
     * @param {number} ms Milliseconds (non-negative number).
     * @returns {void}
     */
    setGlobalFlagDelay (ms) {
        if (Number.isFinite(ms) && ms >= 0) this._globalFlagDelay = Number(ms);
    }

    /**
     * Compute scrollbar width once and cache it for later calls.
     * @returns {number}
     */
    getScrollbarWidth () {
        if (this._cachedScrollbarWidth != null) return this._cachedScrollbarWidth;
        const body = document?.body;
        if (!body) return 0;
        const probe = document.createElement('div');
        probe.style.visibility = 'hidden';
        probe.style.overflow = 'scroll';
        probe.style.width = '100px';
        probe.style.height = '100px';
        body.appendChild(probe);
        const width = probe.offsetWidth - probe.clientWidth;
        body.removeChild(probe);
        this._cachedScrollbarWidth = width;
        return width;
    }

    // ========= Backwards-compatible aliases =========

    /** @deprecated Use `lock()` instead. */
    disableScroll () { this.lock(); }
    /** @deprecated Use `unlock()` instead. */
    enableScroll () { this.unlock(); }
}

// Keep default singleton export for convenience and backward compatibility
const scrollLock = new ScrollLock();
export default scrollLock;
export { ScrollLock };
